import {StackableComponent, Visibility} from 'common/components/base/stackable';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import template from './index.jade';
import css from './index.styl?inline';

export enum ModalType {
  Popup = `popup`,
  Modal = `modal`,
  Drawer = `drawer`,
}

export enum VerticalAlignment {
  Top = `top`,
  Center = `center`,
  Bottom = `bottom`,
}

interface ModalAttrs {
  closeable?: boolean;
  'grow-to-content'?: boolean;
  'hide-close-button'?: boolean;
  'modal-type'?: ModalType;
  'modal-title'?: string;
  'modal-description'?: string;
  'not-fullscreen'?: boolean;
  open: boolean;
  'vertical-alignment'?: VerticalAlignment;
}

/**
 * Defines space allocated for the scrollbar when a
 * modal + backdrop comes in and out of view
 */
const SCROLLBAR_WIDTH_CSS_VARIABLE = `--content-scrollbar-width`;
let documentScrollbarWidth = ``;
let documentScrollRAFId = null;

class Modal extends StackableComponent<EmptyObj, ModalAttrs> {
  private scrollRAFId = null;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      closeable: `boolean`,
      'grow-to-content': `boolean`,
      'hide-close-button': `boolean`,
      'modal-type': {type: `string`, default: ModalType.Modal, enum: Object.values(ModalType)},
      'modal-title': `string`,
      'modal-description': `string`,
      'not-fullscreen': `boolean`,
      open: `boolean`,
      'vertical-alignment': {type: `string`, default: VerticalAlignment.Center, enum: Object.values(VerticalAlignment)},
    };
  }

  get config() {
    return {
      ...super.config,
      css,
      template,
      useShadowDom: true,
      helpers: {
        ModalType,
        VerticalAlignment,
        isClosed: () => this.state.visibility === Visibility.Closed,
        showBackdrop: () => [ModalType.Modal, ModalType.Drawer].includes(this.attr(`modal-type`)),
        handleCloseClick: () => this.close(),
        handleBackdropClick: () => {
          if (this.attr(`closeable`) || this.attr(`modal-type`) === ModalType.Drawer) {
            this.close();
          }
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.attr(`open`)) {
      this.open();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // only re-enable document scrolling if the same modal that disabled it goes out of view
    if (documentScrollRAFId === this.scrollRAFId) {
      this.setDocumentScroll(true, {force: true});
    }
  }

  attributeChangedCallback(attr, newValue, oldValue) {
    super.attributeChangedCallback(attr, newValue, oldValue);

    if (this.initialized && attr === `open`) {
      if (this.attr(`open`)) {
        this.open();
      } else {
        this.close();
      }
    }
  }

  get shouldCloseWhenClickedOutside() {
    return false;
  }

  protected isCloseable() {
    return this.attr(`closeable`);
  }

  // TODO: only modals that set `closeable` deal with the change event, standarize
  // mp-modal usage to always handle the change event
  get shouldCloseOnEsc() {
    return this.attr(`closeable`);
  }

  /** Prevents body scrolling and content shifting from removing document scrolling */
  setDocumentScroll(enabled: boolean, {force = false} = {}) {
    // prevent any other pending scrolling frame update
    if (documentScrollRAFId && documentScrollRAFId !== this.scrollRAFId) {
      cancelAnimationFrame(documentScrollRAFId);
      documentScrollRAFId = null;
    }

    if (force || this.helpers.showBackdrop()) {
      this.scrollRAFId = documentScrollRAFId = window.requestAnimationFrame(() => {
        if (enabled) {
          document.body.style.removeProperty(SCROLLBAR_WIDTH_CSS_VARIABLE);
          document.body.classList.remove(`on-modal`);
        } else {
          documentScrollbarWidth = documentScrollbarWidth || `${window.innerWidth - document.body.clientWidth}px`;
          document.body.style.setProperty(SCROLLBAR_WIDTH_CSS_VARIABLE, documentScrollbarWidth);
          document.body.classList.add(`on-modal`);
        }
      });
    }
  }

  protected setVisibility(visibility: Visibility) {
    super.setVisibility(visibility);

    switch (visibility) {
      case Visibility.Opening:
        this.setDocumentScroll(false);
        break;
      case Visibility.Closed:
        this.setDocumentScroll(true);
        break;
      default:
        break;
    }
  }
}

export const ModalEl = defineAndMakeJsxFactory<{attrs: ModalAttrs}>(`mp-modal`, Modal);
